import * as React from 'react';
import { useSelector } from 'react-redux';
import { OccupantByPermission, PropertyByPermission, UserPermissions } from '@centecomm/portal-service';

import { PropertyTenantResolution } from '../../../State';

import { filterId } from '../../../utils';

import { usePropertyTenantResolution } from '../usePropertyTenantResolution';

type SelectFromAllOccupantsHook = (
    permission: UserPermissions,
) => [
    PropertyByPermission[],
    OccupantByPermission[],
    number[],
    PropertyByPermission[],
    boolean,
    (occupantIds: number[], operator?: string) => void,
    (searchTerm: string) => void,
];

const useSelectFromAllOccupants: SelectFromAllOccupantsHook = (permission: UserPermissions) => {
    const [propertiesAreLoaded] = usePropertyTenantResolution(permission);

    const allProperties: PropertyByPermission[] = useSelector(PropertyTenantResolution.selectors.sortedPropertiesList);

    const [allOccupants, setAllOccupants] = React.useState<OccupantByPermission[]>([]);
    const [selectedOccupants, setSelectedOccupants] = React.useState<number[]>([]);
    const [searchTerm, setSearchTerm] = React.useState<string>('');

    React.useEffect(() => {
        setAllOccupants(
            allProperties
                .map(({ occupants }: PropertyByPermission) => occupants as OccupantByPermission[])
                .reduce((agg: OccupantByPermission[], curr: OccupantByPermission[]) => [...agg, ...curr], []),
        );
    }, [propertiesAreLoaded, allProperties.length]);

    const toggleOccupants = (occupantIds: number[], operator?: string): void => {
        let filteredOccupants: number[] = [];

        switch (operator) {
            case 'add':
                const occupantIdSetAdd = new Set([...selectedOccupants, ...occupantIds]);

                occupantIdSetAdd.forEach((id) => {
                    filteredOccupants.push(id);
                });

                break;
            case 'remove':
                const occupantIdSetRemove = new Set(occupantIds);

                filteredOccupants = selectedOccupants.filter((id) => !occupantIdSetRemove.has(id));
                break;
            default:
                filteredOccupants = occupantIds.reduce((agg: number[], number) => filterId(number, agg), selectedOccupants);
                break;
        }

        setSelectedOccupants(filteredOccupants);
    };

    const filteredProperties = React.useMemo(
        () => allProperties.filter((o) => o.name.toLowerCase().includes(searchTerm.toLowerCase())),
        [searchTerm, allProperties],
    );

    return [
        allProperties,
        allOccupants,
        selectedOccupants,
        filteredProperties,
        propertiesAreLoaded,
        toggleOccupants,
        setSearchTerm,
    ];
};

export default useSelectFromAllOccupants;
